/**
 * Inventory type mirrors beyond the movement-list types already in
 * inventoryMovements.ts — see apps/api/src/inventory/inventory.controller.ts.
 */
export interface RationalQuantity {
  readonly numerator: string;
  readonly denominator: string;
}

export function quantityToNumber(q: RationalQuantity): number {
  const denom = Number(q.denominator);
  return denom === 0 ? 0 : Number(q.numerator) / denom;
}

export interface StockBalanceRow {
  readonly productId: string;
  readonly productCode: string | null;
  readonly warehouseId: string;
  readonly warehouseCode: string | null;
  readonly warehouseLocationId: string;
  readonly lotId: string | null;
  readonly serialId: string | null;
  readonly onHand: RationalQuantity;
  readonly reserved: RationalQuantity;
  readonly blocked: RationalQuantity;
  readonly available: RationalQuantity;
}

export interface StockBalancesResponse {
  readonly balances: readonly StockBalanceRow[];
}

export type ReservationStatus = 'active' | 'released' | 'consumed';

export interface Reservation {
  readonly reservationId: string;
  readonly productId: string;
  readonly productCode?: string | null;
  readonly productName?: string | null;
  readonly warehouseId: string;
  readonly warehouseCode?: string | null;
  readonly warehouseName?: string | null;
  readonly warehouseLocationId: string;
  readonly quantity: RationalQuantity;
  readonly status: ReservationStatus;
  readonly sourceModule: string;
  readonly sourceType: string;
  readonly sourceReference: string;
  readonly consumedMovementId: string | null;
  readonly expiresAt?: string | null;
}

export interface ReservationListResponse {
  readonly reservations: readonly Reservation[];
}

export interface FinanceHandoffLine {
  readonly productId: string;
  readonly quantityNumerator: string;
  readonly quantityDenominator: string;
  readonly valueRial: string;
}

export interface FinanceHandoff {
  readonly movementId: string;
  readonly movementType: string;
  readonly businessDate: string;
  readonly totalValueRial: string;
  readonly lines: readonly FinanceHandoffLine[];
}

export interface MovementLineDetail {
  readonly movementLineId: string;
  readonly rowNumber: number;
  readonly productId: string;
  readonly productCode: string;
  readonly productName: string;
  readonly uomCode: string | null;
  readonly uomName: string | null;
  readonly warehouseId: string;
  readonly warehouseCode: string;
  readonly warehouseName: string;
  readonly locationId: string;
  readonly locationCode: string;
  readonly locationName: string;
  readonly counterWarehouseId: string | null;
  readonly counterWarehouseCode: string | null;
  readonly counterWarehouseName: string | null;
  readonly counterLocationId: string | null;
  readonly counterLocationCode: string | null;
  readonly counterLocationName: string | null;
  readonly lotId: string | null;
  readonly lotCode: string | null;
  readonly serialId: string | null;
  readonly serialNumber: string | null;
  readonly quantityNumerator: string;
  readonly quantityDenominator: string;
  readonly unitCostRial: string | null;
  readonly direction: string;
}

export interface MovementDetail {
  readonly movementId: string;
  readonly movementNumber: string;
  readonly movementType: string;
  readonly status?: string;
  readonly businessDate: string;
  readonly sourceModule?: string;
  readonly sourceType?: string;
  readonly sourceReference?: string | null;
  readonly partyId?: string | null;
  readonly partyCode?: string | null;
  readonly partyName?: string | null;
  readonly lines?: readonly MovementLineDetail[];
  readonly financeHandoff: FinanceHandoff;
}

export interface CardexRow {
  readonly movementId: string;
  readonly movementNumber: string;
  readonly movementType: string;
  readonly businessDate: string;
  readonly partyName: string | null;
  readonly sourceReference: string | null;
  readonly direction: 'in' | 'out';
  readonly inQty: RationalQuantity | null;
  readonly inUnitCostRial: string | null;
  readonly inTotalRial: string | null;
  readonly outQty: RationalQuantity | null;
  readonly outUnitCostRial: string | null;
  readonly outTotalRial: string | null;
  readonly balanceQty: RationalQuantity;
  readonly balanceUnitCostRial: string;
  readonly balanceTotalRial: string;
}

export interface CardexReport {
  readonly productId: string;
  readonly productCode: string;
  readonly productName: string;
  readonly warehouseId: string | null;
  readonly warehouseName: string | null;
  readonly fromDate: string;
  readonly toDate: string;
  readonly openingBalance: {
    readonly quantity: RationalQuantity;
    readonly unitCostRial: string;
    readonly totalValueRial: string;
  };
  readonly rows: readonly CardexRow[];
  readonly totals: {
    readonly totalInQty: RationalQuantity;
    readonly totalInValueRial: string;
    readonly totalOutQty: RationalQuantity;
    readonly totalOutValueRial: string;
    readonly closingQuantity: RationalQuantity;
    readonly closingUnitCostRial: string;
    readonly closingValueRial: string;
  };
}

export interface ReorderAlertRow {
  readonly productId: string;
  readonly productCode: string;
  readonly productName: string;
  readonly uomName: string | null;
  readonly onHand: RationalQuantity;
  readonly reserved: RationalQuantity;
  readonly available: RationalQuantity;
  readonly reorderPoint: RationalQuantity | null;
  readonly minQty: RationalQuantity | null;
  readonly maxQty: RationalQuantity | null;
  readonly status: 'critical' | 'reorder' | 'surplus' | 'normal';
  readonly suggestedOrderQty: RationalQuantity | null;
}

export interface ReorderAlertsResponse {
  readonly alerts: readonly ReorderAlertRow[];
}

export interface StagnantStockItem {
  readonly productId: string;
  readonly productCode: string;
  readonly productName: string;
  readonly uomName: string | null;
  readonly onHand: RationalQuantity;
  readonly lastMovementDate: string | null;
  readonly daysIdle: number;
}

export interface StagnantStockResponse {
  readonly items: readonly StagnantStockItem[];
}

export interface CountSessionItem {
  readonly sessionId: string;
  readonly sessionNumber: string;
  readonly warehouseId: string;
  readonly warehouseCode: string;
  readonly warehouseName: string;
  readonly status: 'draft' | 'in_progress' | 'reconciled' | 'posted' | 'cancelled';
  readonly sessionDate: string;
  readonly description: string;
  readonly createdBy: string;
  readonly postedMovementId: string | null;
  readonly totalLines: number;
  readonly varianceLines: number;
  readonly totalVarianceValueRial: string;
  readonly createdAt: string;
}

export interface CountSessionLineItem {
  readonly countLineId: string;
  readonly rowNumber: number;
  readonly productId: string;
  readonly productCode: string;
  readonly productName: string;
  readonly warehouseLocationId: string;
  readonly locationCode: string;
  readonly locationName: string;
  readonly lotId: string | null;
  readonly lotCode: string | null;
  readonly serialId: string | null;
  readonly serialNumber: string | null;
  readonly systemQty: RationalQuantity;
  readonly countedQty: RationalQuantity | null;
  readonly varianceQty: RationalQuantity;
  readonly unitCostRial: string | null;
  readonly varianceValueRial: string | null;
  readonly notes: string;
}

export interface CountSessionDetailResponse {
  readonly session: CountSessionItem;
  readonly lines: readonly CountSessionLineItem[];
}
